<template>
  <div class="chart-container flex">
    <div style="width: 100%; position: relative">
        <template v-if="chartName === 'LineChart'">
              <line-chart :styles="styles" v-if="!isLoading && !!data" :chartdata="data" :options="options"/>
        </template>
        <template v-else-if="chartName === 'PieChart'">
              <pie-chart :styles="styles" v-if="!isLoading && !!data" :chartdata="data" :options="options"/>
        </template>
        <template v-else-if="chartName === 'BarChart'">
              <bar-chart :styles="styles" v-if="!isLoading && !!data" :chartdata="data" :options="options"/>
        </template>
        <template v-else-if="chartName === 'StackedBarChart'">
              <stacked-bar-chart :styles="styles" v-if="!isLoading && !!data" :chartdata="data" :options="options" :colors="chart_bg"/>
        </template>
        <template v-else-if="chartName === 'FloatingBarChart'">
          <floating-bar-chart :styles="styles" v-if="!isLoading && !!data" :chartdata="data" :options="options" :colors="chart_bg"/>
        </template>
      <div v-if="isChartFundamentallyDifferent" :class="{'chart-overlay': isChartFundamentallyDifferent}">
        <button class="btn-report sparkYellow my-5 uppercase text-bold mx-2 shadow" @click="goto('report-result')">{{ $t('apply') }}</button>
      </div>
    </div>
    <div class="convenient-selector">
      <div class="chart-options">
        <div class="form">
          <label for="chart">{{ $t('chart') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_chart')"></feather></label>
          <multiselect name="chart" v-model="selectedChart" label="name" track-by="id" :multiple="false" :options="allCharts" :searchable="false" :close-on-select="true" :show-labels="false" :placeholder="$t('select')"></multiselect>
        </div>
        <div class="form" v-if="checkAggregateVisible() && chartName === 'StackedBarChart'">
          <label for="stackBy">{{ $t('stack_by') }} <feather type="info" :size='10' stroke='#4b5563' v-tooltip="$t('tooltip_stack_by')"></feather></label>
          <multiselect name="stackBy" :multiple="false" v-model="selectedStackBy" :options="columnsWithoutGroupBy" label="label" track-by="name" :searchable="true" :close-on-select="true" :show-labels="false" :placeholder="$t('select')"></multiselect>
        </div>
        <span v-if="isChartFundamentallyDifferent" class="tip">{{ $t('tooltip_chart_rerun') }}</span>
      </div>

    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'

import LineChart from './chart-types/LineChart'
import BarChart from './chart-types/BarChart'
import PieChart from './chart-types/PieChart'
import StackedBarChart from "@/components/chart-types/StackedBarChart.vue";
import FloatingBarChart from "@/components/chart-types/FloatingBarChart.vue";
import {mapActions, mapGetters, mapMutations} from 'vuex'
import ModalService from "@/modules/modals/services/modal.service";
import ModalSomethingWentWrong from "@/components/ModalSomethingWentWrong.vue";
import Vue from "vue";

export default {
      name: 'ChartContainer',
      components: { LineChart, BarChart, PieChart, StackedBarChart, FloatingBarChart },
      props: {
            data: {
                  type: Object,
                  required: false,
                  default: null,
            },
      },
      data(){
        let xLabels = [];
        this.data.datasets.forEach(function (dataset) {
          if(dataset.label != undefined){
            xLabels.push(dataset.label)
          }
        })
        let xAxes =
          {
            offset: true,
            gridLines: {
              offsetGridLines: true
            },
          };

        let xAxes2 =
          {
            labels: xLabels,
            offset: true,
            gridLines: {
              offsetGridLines: true
            },
          };

        if(this.data.sublabels != undefined){
          xAxes = xAxes2;
        }

        return {
          chart_bg: ["rgba(0, 40, 58, 1)", "rgba(251, 224, 37, 1)", "rgba(123, 152, 172, 1)","rgba(226, 226, 226, 1)","rgba(241, 242, 242, 1)"],
          chart_border: ["rgba(0, 40, 58, 1)", "rgba(251, 224, 37, 1)", "rgba(123, 152, 172, 1)","rgba(226, 226, 226, 1)","rgba(241, 242, 242, 1)"],
          chart_bg_line: ["rgba(0, 40, 58, 0.1)", "rgba(251, 224, 37, 0.1)", "rgba(123, 152, 172, 0.1)","rgba(226, 226, 226, 0.1)","rgba(241, 242, 242, 0.1)"],
          styles: {
            height: '420px',
            width: 'calc(100% - 400px)',
            margin: '0px auto',
            padding: '0px 1rem',
            position: 'relative'
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {}
              },
              legend: {
                display: false,
              },
            },
            scales: {
              xAxis1: xAxes,
              y:
                {
                    beginAtZero: true,
                }
            }
          }
        }
      },
      computed: {
            ...mapGetters(['isLoading', 'chartName', 'allCharts', 'newReport', 'allColumns', 'lastRunChartType', 'lastRunStackBy', 'lastRunGroupBy']),
            selectedChart: {
              get() {
                if(this.newReport.chart_id !== null) {
                  return this.allCharts.find(chart => chart.id === this.newReport.chart_id)
                } else {
                  return null
                }
              },
              set(value) {
                if(value === null){
                  this.setNewReportItem({chart_id: null})
                  this.setNewReportItem({chart_name: null})
                } else {
                  this.setNewReportItem({chart_id: value.id})
                  this.setNewReportItem({chart_name: value.name})
                }
                if (window.onbeforeunload === null) window.onbeforeunload = (event) => event.preventDefault();
              }
            },
            selectedGroupBy: {
              get() {
                return this.newReport.group_by
              },
              set(value) {
                this.setNewReportItem({group_by: value})
                if (window.onbeforeunload === null) window.onbeforeunload = (event) => event.preventDefault();
              }
            },
            selectedStackBy: {
              get() {
                return this.newReport.stack_by
              },
              set(value) {
                this.setNewReportItem({stack_by: value})
                if (window.onbeforeunload === null) window.onbeforeunload = (event) => event.preventDefault();
              }
            },
            columns (){
              return this.allColumns.filter(column => {
                return this.newReport.service_name == column.service
              }).map(column => {
                return {name: column.name, type: column.type, label: this.$t(column.name)}
              })
            },
            columnsWithoutGroupBy (){
              let columnsWithoutGroupBy = []
              let selectedGroupBy = Array.isArray(this.selectedGroupBy) ? this.selectedGroupBy[0] : this.selectedGroupBy
              if (selectedGroupBy === null) {
                return this.columns
              }
              this.columns.forEach((column) => {
                if (typeof selectedGroupBy === 'object' && column.name === selectedGroupBy.name) {
                  return
                }
                columnsWithoutGroupBy.push(column)
              })
              return columnsWithoutGroupBy
            },
            isChartFundamentallyDifferent (){
              if (this.hasGroupByBeenChanged) return true
              if (this.lastRunChartType === 'StackedBarChart') return this.chartName !== this.lastRunChartType || this.hasStackByBeenChanged
              return this.chartName === 'StackedBarChart'
            },
            hasStackByBeenChanged (){
              if (this.lastRunStackBy === null && this.newReport.stack_by === null) return false
              if (this.lastRunStackBy === null || this.newReport.stack_by === null) return true
              return this.lastRunStackBy.name !== this.newReport.stack_by.name
            },
            hasGroupByBeenChanged (){
              let lastRunGroupBy = this.clarifyGroupBy(this.lastRunGroupBy)
              let newReportGroupBy = this.clarifyGroupBy(this.newReport.group_by)
              if (lastRunGroupBy === null && newReportGroupBy === null) return false
              if (lastRunGroupBy === null || newReportGroupBy === null) return true
              return lastRunGroupBy.name !== newReportGroupBy.name
            },
      },
      methods:{
            ...mapActions({
              generateReport: 'generateReport',
            }),
            ...mapMutations({
              setNewReportItem: 'setNewReportItem',
              setLastRunChartType: 'setLastRunChartType',
              setLastRunStackBy: 'setLastRunStackBy',
              setLastRunGroupBy: 'setLastRunGroupBy',
            }),
            addMissingChartColours(){
              if (this.data){
                let baseColours = [[0x00, 0x28, 0x3A],[0x7B, 0x98, 0xAC],[0xF1, 0xF2, 0xF2],[0xFB, 0xE0, 0x25]]
                const chartLabelsCount = this.chartName === 'StackedBarChart' ? this.data.datasets.length : this.data.labels.length
                const baseColourCount = baseColours.length
                let missingColoursCount = this.chartName === 'StackedBarChart' ? Math.min(chartLabelsCount - this.chart_bg.length, Vue.Constants.STACKING_THRESHOLD - baseColourCount) : (chartLabelsCount - this.chart_bg.length)
                let generationCount = missingColoursCount / (baseColourCount*(baseColourCount-1)/2)
                const opacities = ['1','1','0.1']
                if (missingColoursCount > 0) {
                  let newColours = []
                  for (let j = 0; j < baseColourCount; j++) {
                    for (let k = 0; k < baseColourCount; k++) {
                      if (j > k) {
                        let gradientColours = this.makeGradientColours(baseColours[j], baseColours[k], generationCount)
                        for (let i = 0; i < generationCount; i++) {
                          const formattedColour = []
                          let rgbPartialString = gradientColours[i][0] + ',' + gradientColours[i][1] + ',' + gradientColours[i][2];
                          for (let opacity of opacities){
                            formattedColour.push('rgba(' + rgbPartialString + ',' + opacity + ')')
                          }
                          newColours.push(formattedColour)
                        }
                      }
                    }
                  }

                  newColours.map(value => ({ value, sort: Math.random() }))
                      .sort((a, b) => a.sort - b.sort)
                      .map(({ value }) => value)
                      .forEach(colour => {
                        this.chart_bg.push(colour[0])
                        this.chart_border.push(colour[1])
                        this.chart_bg_line.push(colour[2])
                      })
                }
              }
            },
            makeIntermediateValues(a, b, count){
              let min = Math.min(a, b)
              let max = Math.max(a, b)
              let diff = max - min
              let step = ~~(diff/(count+1))
              let output = []
              for (let i=0; i<count; i++) {
                output.push((i + 1)*step + min)
              }
              return output
            },
            makeGradientColours(a, b, count) {
              let rs = this.makeIntermediateValues(a[0], b[0], count)
              let gs = this.makeIntermediateValues(a[1], b[1], count)
              let bs = this.makeIntermediateValues(a[2], b[2], count)
              let newColours = []
              for (let i=0; i<rs.length; i++) {
                newColours.push([rs[i],gs[i],bs[i]])
              }
              return newColours
            },
            checkAggregateVisible() {
                  return this.selectedGroupBy!==null && (typeof this.selectedGroupBy === 'object' && !Array.isArray(this.selectedGroupBy) && this.selectedGroupBy.name != null || Array.isArray(this.selectedGroupBy) && this.selectedGroupBy.length>0)
            },
            goto(el){
              this.isLoading = true
              const payload = {
                backward_interval: this.newReport.backward_interval,
                columns: this.newReport.view_columns_stringify,
                filters: this.newReport.filters_stringify,
                group_by: this.newReport.group_by_stringify,
                stack_by: this.newReport.stack_by_stringify,
                order_by: this.newReport.order_by_stringify,
                date_from: this.newReport.date_from,
                date_to: this.newReport.date_to,
                cover_type: this.newReport.cover_type,
                chart_id: this.newReport.chart_id,
                map: this.newReport.map ? this.newReport.map : false,
                service: this.newReport.service_name,
                aggregate: this.newReport.aggregate ? this.newReport.aggregate_stringify : null,
                additionalGroupBy: this.newReport.additionalGroupBy ? this.newReport.additionalGroupBy_stringify : null
              }
              this.generateReport(payload).then(()=> {
                document.getElementById(el).scrollIntoView({behavior: 'smooth', block: 'start', inline: 'center'})
                this.setLastRunChartType(this.chartName)
                this.setLastRunStackBy(this.newReport.stack_by)
                this.setLastRunGroupBy(this.newReport.group_by)
              }).catch(()=>{
                ModalService.open(ModalSomethingWentWrong)
              }).finally(()=> {
                this.isLoading = false
              })
            },
            clarifyGroupBy (groupBy){
              if (groupBy === null || (Array.isArray(groupBy) && groupBy.length === 0)) {
                return null
              }
              if (Array.isArray(groupBy)) {
                return groupBy[0]
              }
              return groupBy
            },
      },
      created(){
            //assing to Chart defaults
            this.addMissingChartColours()
            Chart.defaults.elements.point.backgroundColor = this.chart_bg
            Chart.defaults.elements.point.borderColor = this.chart_border
            Chart.defaults.elements.arc.backgroundColor = this.chart_bg
            Chart.defaults.elements.arc.borderColor = this.chart_border
            Chart.defaults.elements.bar.backgroundColor = this.chart_bg
            Chart.defaults.elements.bar.borderColor = this.chart_border
            Chart.defaults.elements.bar.borderWidth = 1
            Chart.defaults.elements.line.backgroundColor = this.chart_bg_line
            Chart.defaults.elements.line.borderColor = this.chart_bg
            Chart.defaults.elements.line.fill = false
      }
}
</script>

<style lang="sass">
.chart-overlay
      position: absolute
      height: 100%
      width: calc(100% - 2rem)
      background: rgba(255, 255, 255, 0.9)
      top: 0
      left: 1rem
      display: flex
      justify-content: center
      align-items: center
      backdrop-filter: grayscale(100%) blur(5px)
.convenient-selector
      position: relative
      right: 200px
      .chart-options
            position: absolute
            width: 200px
.tip
      @apply block
      @apply text-gray-500
      @apply text-xs
      @apply font-normal
      margin-top: 16px
</style>